import { REMOTE_PLAY_ENDPOINT } from "@/config";

export type Pad = { i: number; b: number[]; a: number[] }; // pad index, buttons (standard mapping), axes
export type Status = 'connecting' | 'connected' | 'reconnecting' | 'host-left';

// Remote signaling server
const SIGNAL: string = REMOTE_PLAY_ENDPOINT;
const MAX_BITRATE = 8_000_000;

/** Never trust remote input: clamp sizes and ranges before it reaches the OS gamepad driver. */
export function sanitizePad(x: unknown): Pad | null {
  const o = x as any;
  if (!o || typeof o !== 'object' || !Number.isInteger(o.i) || o.i < 0 || o.i > 15) return null;
  const arr = (v: unknown, max: number, lo: number, hi: number) =>
    Array.isArray(v)
      ? v
          .slice(0, max)
          .map((n) => (typeof n === 'number' && isFinite(n) ? Math.min(hi, Math.max(lo, n)) : 0))
      : [];
  return { i: o.i, b: arr(o.b, 20, 0, 1), a: arr(o.a, 8, -1, 1) };
}

type Handlers = {
  onStream?: (s: MediaStream) => void;
  onPad?: (peer: string, p: Pad) => void;
  onLeave?: (peer: string) => void;
  onStatus?: (s: Status) => void;
  onFatal?: (reason: string) => void;
  onEnded?: () => void; // host stopped sharing
};

function preferH264(pc: RTCPeerConnection) {
  // hardware-friendly, lower latency; falls back if unsupported
  try {
    const caps = RTCRtpSender.getCapabilities('video');
    if (!caps) return;
    const h264 = caps.codecs.filter((c) => c.mimeType === 'video/H264');
    if (!h264.length) return;
    const rest = caps.codecs.filter((c) => c.mimeType !== 'video/H264');
    pc.getTransceivers().forEach((t) => {
      if (t.sender.track?.kind === 'video') t.setCodecPreferences([...h264, ...rest]);
    });
  } catch {
    /* unsupported: keep defaults */
  }
}

async function capBitrate(pc: RTCPeerConnection) {
  for (const s of pc.getSenders()) {
    if (s.track?.kind !== 'video') continue;
    try {
      const p: any = s.getParameters();
      if (!p.encodings?.length) p.encodings = [{}];
      p.encodings[0].maxBitrate = MAX_BITRATE;
      p.degradationPreference = 'maintain-framerate';
      await s.setParameters(p);
    } catch {
      /* best effort */
    }
  }
}

export class Session {
  private ws?: WebSocket;
  private ice: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];
  private pcs = new Map<string, RTCPeerConnection>();
  private chans = new Map<string, RTCDataChannel>();
  private stream?: MediaStream;
  private q: Promise<void> = Promise.resolve();
  private stopped = false;
  private attempt = 0;
  private timer?: ReturnType<typeof setTimeout>;
  private hostId: string | null = null;

  constructor(
    private room: string,
    private isHost: boolean,
    private h: Handlers
  ) {}

  async start() {
    if (this.isHost) {
      this.stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 60 },
        audio: true,
      });
      this.stream.getVideoTracks().forEach((t) => {
        t.contentHint = 'motion';
        t.onended = () => this.h.onEnded?.();
      });
    }
    this.connect();
  }

  /** guest -> host over the unreliable, unordered data channel */
  sendPad(p: Pad) {
    const s = JSON.stringify(p);
    this.chans.forEach((c) => {
      if (c.readyState === 'open') c.send(s);
    });
  }

  stop() {
    this.stopped = true;
    clearTimeout(this.timer);
    this.ws?.close();
    this.dropAll();
    this.stream?.getTracks().forEach((t) => t.stop());
  }

  private connect() {
    this.h.onStatus?.(this.attempt ? 'reconnecting' : 'connecting');
    const ws = new WebSocket(SIGNAL);
    this.ws = ws;
    ws.onopen = () => ws.send(JSON.stringify({ type: 'join', room: this.room, host: this.isHost }));
    ws.onmessage = (e) => {
      let m: any;
      try {
        m = JSON.parse(e.data);
      } catch {
        return;
      }
      switch (m.type) {
        case 'welcome':
          this.attempt = 0;
          this.ice = m.ice ?? this.ice;
          this.hostId = m.hostId ?? null;
          this.h.onStatus?.('connected');
          if (this.isHost) (m.peers as string[]).forEach((p) => void this.offer(p));
          break;
        case 'peer-joined':
          if (m.host) {
            this.hostId = m.id;
            this.h.onStatus?.('connected');
          } else if (this.isHost) void this.offer(m.id);
          break;
        case 'peer-left':
          this.drop(m.id);
          break;
        case 'signal':
          this.q = this.q.then(() => this.onSignal(m.from, m.data)).catch(() => {});
          break;
      }
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws || this.stopped) return;
      this.dropAll();
      if (ev.code >= 4000 && ev.code < 5000) {
        this.stopped = true;
        this.h.onFatal?.(ev.reason || String(ev.code));
        return;
      }
      this.h.onStatus?.('reconnecting');
      this.timer = setTimeout(() => this.connect(), Math.min(10000, 500 * 2 ** this.attempt++));
    };
  }

  private signal(to: string, data: unknown) {
    if (this.ws?.readyState === WebSocket.OPEN)
      this.ws.send(JSON.stringify({ type: 'signal', to, data }));
  }

  private mk(peer: string) {
    const pc = new RTCPeerConnection({ iceServers: this.ice });
    this.pcs.set(peer, pc);
    pc.onicecandidate = (e) => {
      if (e.candidate) this.signal(peer, { ice: e.candidate });
    };
    pc.ontrack = (e) => {
      try {
        (e.receiver as any).jitterBufferTarget = 0;
      } catch {
        /* not supported */
      }
      this.h.onStream?.(e.streams[0]);
    };
    pc.onconnectionstatechange = () => {
      if (this.pcs.get(peer) === pc && pc.connectionState === 'failed' && this.isHost)
        void this.offer(peer); // retry fresh
    };
    return pc;
  }

  private async offer(peer: string) {
    try {
      this.pcs.get(peer)?.close();
      const pc = this.mk(peer);
      for (const t of this.stream!.getTracks()) pc.addTrack(t, this.stream!);
      preferH264(pc);
      const ch = pc.createDataChannel('input', { ordered: false, maxRetransmits: 0 });
      ch.onmessage = (e) => {
        let p: Pad | null = null;
        try {
          p = sanitizePad(JSON.parse(e.data));
        } catch {
          return;
        }
        if (p) this.h.onPad?.(peer, p);
      };
      await pc.setLocalDescription(await pc.createOffer());
      await capBitrate(pc);
      this.signal(peer, { sdp: pc.localDescription });
    } catch (err) {
      console.error('offer failed', err);
    }
  }

  private async onSignal(from: string, d: any) {
    let pc = this.pcs.get(from);
    if (d?.sdp) {
      if (d.sdp.type === 'offer') {
        // guests only ever receive fresh offers from the host
        pc?.close();
        pc = this.mk(from);
        pc.ondatachannel = (e) => this.chans.set(from, e.channel);
      }
      if (!pc) return;
      await pc.setRemoteDescription(d.sdp);
      if (d.sdp.type === 'offer') {
        await pc.setLocalDescription(await pc.createAnswer());
        this.signal(from, { sdp: pc.localDescription });
      }
    } else if (d?.ice && pc) await pc.addIceCandidate(d.ice).catch(() => {});
  }

  private drop(peer: string) {
    this.pcs.get(peer)?.close();
    this.pcs.delete(peer);
    this.chans.delete(peer);
    this.h.onLeave?.(peer);
    if (!this.isHost && peer === this.hostId) this.h.onStatus?.('host-left');
  }

  private dropAll() {
    [...this.pcs.keys()].forEach((p) => this.drop(p));
  }
}
